<template>
    <div class="tooltip-wrapper">
        <div
            ref="tooltip"
            class="tl-tooltip"
            :class="{ secondary: secondary }"
            :style="tooltipStyles"
        >
            <slot name="tooltip">
                <div v-html="marked(tip)" />
            </slot>
        </div>
        <div
            :ref="setTooltipContainer"
            class="tooltip-container"
            @mouseenter="handleMouseEnter"
            @mouseleave="handleMouseLeave"
            @focus="showTooltipDelay"
            @blur="hideTooltip"
        >
            <slot />
            <div
                v-if="isTouchDevice"
                :ref="setTooltipContainer"
                class="tooltip-icon"
            >
                <i
                    class="icon mi-circle-information"
                    @touchstart="handleTouchStart"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, type ComponentPublicInstance } from 'vue'
import { type Strategy, useFloating, flip, shift, offset } from '@floating-ui/vue'
import { onClickOutside, useEventBus } from '@vueuse/core'
import { getScrollParent } from 'widget-components/utilities/get-scroll-parent'
import { getCssVar } from 'widget-components/utilities/getCssVar'
import marked from 'widget-components/utilities/marked'

type TooltipProps = {
    tip?: string
    placement?: 'top' | 'left' | 'right' | 'bottom'
    delay?: number | string
    strategy?: Strategy
    disabled?: boolean
    zIndex?: number | string
    secondary?: boolean
}

const props = defineProps<TooltipProps>()

const tooltipContainer = ref<HTMLElement | null>()
const tooltip = ref<HTMLElement>()
const isOpened = ref<boolean>(false)
const delayTimeout = ref<number>()
const isTouchDevice = ref<boolean>('ontouchstart' in window || navigator.maxTouchPoints > 0)
let stopClickOutsideVueUse: () => void | undefined
const componentId = Symbol('componentId')

const { emit, once } = useEventBus<'tooltip-show', TooltipEventBusEventPayload>('widget-components-tooltip-show')

type TooltipEventBusEventPayload = {
    componentId: symbol
}

const { floatingStyles, update } = useFloating(tooltipContainer, tooltip, {
    placement: props.placement,
    strategy: props.strategy ? props.strategy : 'absolute',
    middleware: [flip(), shift(), offset(isTouchDevice.value ? -5 : 5)],
})

const tooltipStyles = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const styles: Record<string, any> = {
        ...floatingStyles.value,
    }

    if (isOpened.value) {
        styles.zIndex = props.zIndex ?? getCssVar('--layer-component-tooltip')
        styles.opacity = '1'
        styles.visibility = 'visible'
        styles.pointerEvents = 'all'
    } else {
        styles.zIndex = '-1'
        styles.opacity = '0'
        styles.visibility = 'hidden'
        styles.pointerEvents = 'none'
    }

    return styles
})

const showTooltip = () => {
    if (isOpened.value) return

    isOpened.value = true
    update()
    emit('tooltip-show', {
        componentId: componentId,
    })

    if (isTouchDevice.value) {
        once((event, data) => {
            if (event === 'tooltip-show') {
                if (data?.componentId && data.componentId !== componentId && isOpened.value) {
                    hideTooltip()
                }
            }
        })
        stopClickOutsideVueUse = onClickOutside(tooltipContainer, hideTooltip)
        getScrollParent(tooltipContainer.value!)?.addEventListener('scroll', hideTooltip, { passive: true })
    }
}

const setTooltipContainer = (val: Element | ComponentPublicInstance | null) => {
    if (!val) return

    const el = val as HTMLElement
    tooltipContainer.value = isTouchDevice.value && el.classList.contains('tooltip-icon') ? el : el
}

const clearCurrentTimeout = () => {
    if (delayTimeout.value !== undefined) clearTimeout(delayTimeout.value)
}

const handleMouseEnter = () => {
    if (isTouchDevice.value || isOpened.value) {
        return
    }
    showTooltipDelay()
}

const handleMouseLeave = () => {
    if (isTouchDevice.value) {
        return
    }
    hideTooltip()
}

const handleTouchStart = (event: TouchEvent) => {
    event.stopPropagation()

    if (!isTouchDevice.value) {
        return
    }

    if (isOpened.value) {
        hideTooltip()
        return
    }

    showTooltipDelay()
}

function showTooltipDelay() {
    if (props.disabled) {
        return
    }

    if (props.delay) {
        clearCurrentTimeout()
        delayTimeout.value = window.setTimeout(showTooltip, Number(props.delay))
    } else {
        showTooltip()
    }
}

function hideTooltip() {
    clearCurrentTimeout()
    isOpened.value = false
    if (stopClickOutsideVueUse) {
        stopClickOutsideVueUse()
    }
    getScrollParent(tooltipContainer.value!)?.removeEventListener('scroll', hideTooltip)
}
</script>

<style lang="sass" scoped>
.tooltip-wrapper
    width: 100%

.tl-tooltip
    max-width: 400px
    background: var(--tl-tooltip-bg-color)
    color: var(--tl-tooltip-text-color)
    padding: var(--tl-tooltip-padding-y) var(--tl-tooltip-padding-x)
    border-radius: 8px
    transition: opacity .3s ease-in-out
    font-size: var(--tl-tooltip-font-size)
    font-weight: var(--tl-tooltip-font-weight, normal)
    word-break: break-word
    white-space: pre-wrap

    :deep(.md-h1),
    :deep(.md-h2),
    :deep(.md-h3),
    :deep(.md-h4),
    :deep(.md-h5),
    :deep(.md-h6)
        font-weight: var(--font-weight-bold)
        margin: 0

    :deep(.md-h1)
        font-size: 1.4em

    :deep(.md-h2)
        font-size: 1.2em

    :deep(.md-h3)
        font-size: 1.1em

    &.secondary
        background: var(--color-white)
        padding: 8px
        border-radius: 8px
        border: 1px solid var(--color-black-tint-light)
        box-shadow: 0 0 10px 0 var(--color-black-tint-medium)
        color: var(--color-black)

@media (hover: none)
    .tooltip-container
        position: relative
        flex: 1
        padding-right: 25px

.tooltip-icon
    position: absolute
    top: 0
    right: 0
    margin: -15px
    padding: 15px
    z-index: 100
</style>
